import { Component, input, output, signal } from '@angular/core';
import { PropertyGroupItem } from '../property-group-item/property-group-item';

export interface PropertyGroupListItem {
  name: string;
  type: string;
}

@Component({
  selector: 'cmi-property-group-list',
  imports: [PropertyGroupItem],
  templateUrl: './property-group-list.html'
})
export class PropertyGroupList {
  public readonly groups = input.required<PropertyGroupListItem[]>();
  public readonly deletingGroupName = input<string | null>(null);
  public readonly isDraggable = input<boolean>(false);

  public readonly draggingName = signal<string | null>(null);
  public readonly dragOverName = signal<string | null>(null);
  public readonly dragOverPosition = signal<'before' | 'after'>('before');

  public readonly reorder = output<string[]>();
  public readonly edit = output<string>();
  public readonly confirmDelete = output<string>();
  public readonly cancelDelete = output<void>();
  public readonly removed = output<string>();

  public moveUp(groupName: string): void {
    const groups = this.groups();
    const index = groups.findIndex((g) => g.name === groupName);
    if (index <= 0) {
      return;
    }
    const newOrder = groups.map((g) => g.name);
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    this.reorder.emit(newOrder);
  }

  public moveDown(groupName: string): void {
    const groups = this.groups();
    const index = groups.findIndex((g) => g.name === groupName);
    if (index < 0 || index >= groups.length - 1) {
      return;
    }
    const newOrder = groups.map((g) => g.name);
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    this.reorder.emit(newOrder);
  }

  public onDragStart(event: DragEvent, groupName: string): void {
    this.draggingName.set(groupName);
    event.dataTransfer?.setData('text/plain', groupName);
  }

  public onDragOver(event: DragEvent, groupName: string): void {
    event.preventDefault();
    if (groupName !== this.draggingName()) {
      this.dragOverName.set(groupName);
      const el = event.currentTarget as HTMLElement;
      const rect = el.getBoundingClientRect();
      this.dragOverPosition.set(event.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
    }
  }

  public onDrop(event: DragEvent, groupName: string): void {
    event.preventDefault();
    const source = this.draggingName();
    const position = this.dragOverPosition();
    this.draggingName.set(null);
    this.dragOverName.set(null);
    if (!source || source === groupName) {
      return;
    }
    const groups = this.groups();
    const newOrder = groups.map((g) => g.name);
    const fromIndex = newOrder.indexOf(source);
    newOrder.splice(fromIndex, 1);
    const toIndex = newOrder.indexOf(groupName);
    newOrder.splice(position === 'after' ? toIndex + 1 : toIndex, 0, source);
    this.reorder.emit(newOrder);
  }

  public onDragEnd(): void {
    this.draggingName.set(null);
    this.dragOverName.set(null);
  }
}
